import LockIcon from '@mui/icons-material/Lock';
import { Box, Grid, Typography } from '@mui/material';
import { Identifier, TournamentPlayerRole, TournamentPlayerStatus } from '@okletsplay/core';
import { EntityStoreName } from 'app/enums/store';
import { useEntityRepositoryStatus } from 'app/hooks/store';
import { TournamentEntity, TournamentPlayerEntity, UserEntity } from 'app/interfaces/entity';
import { MyTeamPlayersList } from 'modules/tournaments/components/BracketTournament/MyTeam/MyTeamPlayersList';
import { MyTeamWithdrawButton } from 'modules/tournaments/components/BracketTournament/MyTeam/MyTeamWithdrawButton';
import { TournamentSettingsTitle } from 'modules/tournaments/components/TournamentSettingsTitle';
import { useTournamentDetails } from 'modules/tournaments/hooks';
import { useJoinTournamentTeamForm } from 'modules/tournaments/hooks/use-join-team-form';
import { CurrentTournamentStoreStatus } from 'modules/tournaments/store/current-tournament/enum';
import {
  joinTournament,
  updateTournamentTeam,
} from 'modules/tournaments/store/current-tournament/repository';
import { currentTournamentSelector } from 'modules/tournaments/store/current-tournament/selectors';
import { SearchUsersModal } from 'modules/users/components/SearchUsersModal';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { Controller, FormProvider, useFieldArray } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { ErrorMessage } from 'shared/components/Form/ErrorMessage';
import { TextInput } from 'shared/components/Form/TextInput';
import { LoaderButton } from 'shared/components/LoaderButton/LoaderButton';
import { CardAction } from 'shared/interfaces';
import { ActionGuard } from 'shared/components/ActionGuard';
import { sessionSelector } from '../../../../../../app/store/session/selectors';
import { useStyles } from './MyTeamManageView.styles';
import { WithPaidActionButton } from 'modules/game-payments/components';

interface ManageTournamentTeamProps {
  tournament?: TournamentEntity;
}

export const MyTeamManageView: FC<ManageTournamentTeamProps> = ({ tournament }) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const { isLiveTournament } = useTournamentDetails();
  const activeUser = useSelector(sessionSelector.activeUser);
  const myTeam = useSelector(currentTournamentSelector.myTeam);
  const isPrivateTournament = useSelector(currentTournamentSelector.isPrivateTournament);
  const entryFee = useSelector(currentTournamentSelector.tournamentEntryFee);
  const joinedPlayers = useSelector(currentTournamentSelector.joinedPlayers);
  const spectators = useSelector(currentTournamentSelector.spectators);
  const isSinglePlayer = useSelector(currentTournamentSelector.isSinglePlayer);
  const teamSize = useSelector(currentTournamentSelector.teamsSize);

  const joining = useEntityRepositoryStatus({
    [EntityStoreName.CurrentTournament]: CurrentTournamentStoreStatus.Joining,
  });

  const isEditMode = useMemo(() => myTeam, [myTeam]);
  const isPassphraseFieldAvailable = useMemo(
    () => isPrivateTournament && !Boolean(myTeam),
    [isPrivateTournament, myTeam]
  );

  const { form, resetToDefault } = useJoinTournamentTeamForm(
    teamSize,
    isPassphraseFieldAvailable,
    isSinglePlayer
  );
  const {
    watch,
    control,
    reset,
    handleSubmit,
    formState: { isValid, isSubmitted, errors },
  } = form;
  const players = watch('players');

  const { append, remove } = useFieldArray({ control, name: 'players' });

  const onRemovePlayerHandler = useCallback(
    (player: TournamentPlayerEntity) => {
      const playerPosition = form
        ?.getValues()
        ?.players?.findIndex((user) => user?.user?.userId === player?.userId);

      if (playerPosition <= 0) {
        return;
      }

      remove(playerPosition);
    },
    [form, remove]
  );

  const selectedUsers = useMemo(() => players?.map((player) => player?.user), [players]);

  const playerCardActions = useMemo((): CardAction<TournamentPlayerEntity>[] => {
    return [
      {
        label: 'Remove',
        handler: onRemovePlayerHandler,
      },
    ];
  }, [onRemovePlayerHandler]);

  const selectPlayerHandler = useCallback(
    (users: UserEntity[]) => {
      (users ?? []).forEach((user) => {
        const userPosition = form
          ?.getValues()
          ?.players?.findIndex((player) => player?.user?.id === user?.id);

        if (!user || userPosition >= 0) {
          return;
        }

        const teamPlayer = myTeam?.players?.find((player) => player?.user?.userId === user?.userId);
        if (teamPlayer) {
          return append(teamPlayer);
        }

        if (users?.length) {
          append({
            role: TournamentPlayerRole.Player,
            status: TournamentPlayerStatus.Invited,
            userId: user?.userId,
            user,
          });
        }
      });
    },
    [myTeam?.players, form, append]
  );

  const hiddenUsers = useMemo(() => {
    return [...(selectedUsers ?? []), ...(joinedPlayers ?? []), ...(spectators ?? [])];
  }, [joinedPlayers, selectedUsers, spectators]);

  const getFormData = useCallback(() => {
    const formData = form?.getValues();

    return {
      ...formData,
      players: formData?.players?.map((player) => ({ ...player, user: player?.user?.id })),
    };
  }, [form]);

  const onEditHandler = useCallback(() => {
    const tournamentIdentifier = tournament?.id as Identifier;
    const teamId = myTeam?.id as Identifier;
    if (!tournamentIdentifier || !teamId) return;

    dispatch(
      updateTournamentTeam({ identifier: tournamentIdentifier, teamId, data: getFormData() })
    );
  }, [dispatch, getFormData, myTeam?.id, tournament?.id]);

  const onJoinHandler = useCallback(() => {
    const tournamentIdentifier = tournament?.id as Identifier;
    if (!tournamentIdentifier) return;

    dispatch(joinTournament({ identifier: tournamentIdentifier, data: getFormData() }));
  }, [tournament?.id, dispatch, getFormData]);

  useEffect(() => {
    if (!myTeam) {
      resetToDefault();
    } else {
      reset({
        players: myTeam?.players ?? [],
        name: myTeam?.name ?? '',
      });
    }
  }, [myTeam, reset, resetToDefault]);

  return (
    <FormProvider {...form}>
      <Box>
        <TournamentSettingsTitle>
          {isSinglePlayer ? 'Player name' : 'Create Your Team'}
        </TournamentSettingsTitle>
        <Grid container spacing={2} className={classes.controls}>
          <Grid item>
            <Controller
              name="name"
              control={control}
              render={({ field: { ...rest }, fieldState: { error } }) => (
                <Box mb={2}>
                  {!isSinglePlayer ? (
                    <Typography mb={1}>Enter a custom team name:</Typography>
                  ) : null}
                  <TextInput
                    {...rest}
                    placeholder="Player 1’s Team"
                    error={Boolean(error)}
                    disabled={isSinglePlayer}
                  />
                  <ErrorMessage error={error?.message} />
                </Box>
              )}
            />
          </Grid>
          {isPassphraseFieldAvailable && (
            <Grid item>
              <Controller
                name="passphrase"
                control={control}
                render={({ field: { ...rest }, fieldState: { error } }) => (
                  <Box mb={2}>
                    <Box display="flex" justifyContent="space-between">
                      <Typography mb={1}>Enter tournament passphrase:</Typography>
                    </Box>
                    <TextInput
                      {...rest}
                      type="password"
                      placeholder="Passphrase"
                      startIcon={<LockIcon />}
                      error={Boolean(error)}
                    />
                    <ErrorMessage error={error?.message} />
                  </Box>
                )}
              />
            </Grid>
          )}
        </Grid>
      </Box>
      <Box>
        <TournamentSettingsTitle>Active Players</TournamentSettingsTitle>
        {Boolean(entryFee) && (
          <Typography mb={2} variant="subtitle2" color="primary.light">
            Required to pay entry fee. Only active players at the start of the tournament are
            eligible for payouts.
          </Typography>
        )}
        <MyTeamPlayersList
          players={players}
          teamSize={teamSize}
          playerCardActions={playerCardActions}
        />
      </Box>
      {!isLiveTournament && (
        <Box mt={3}>
          <Box mb={2}>
            <MyTeamWithdrawButton tournamentId={tournament?.id} />
          </Box>
          {isEditMode ? (
            <LoaderButton
              fullWidth
              variant="contained"
              color="primary"
              onClick={handleSubmit(onEditHandler)}
              disabled={!isValid && isSubmitted}
              loading={joining}
            >
              Save Changes
            </LoaderButton>
          ) : (
            <WithPaidActionButton
              amount={entryFee}
              label="Join Tournament"
              onClick={handleSubmit(onJoinHandler)}
              isLoading={joining}
            >
              {({ isCanNotEnter, text, onClick }) => (
                <ActionGuard>
                  <LoaderButton
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={onClick}
                    disabled={(!isValid && isSubmitted) || isCanNotEnter}
                    loading={joining}
                  >
                    {text}
                  </LoaderButton>
                </ActionGuard>
              )}
            </WithPaidActionButton>
          )}

          {errors.players?.message && (
            <ErrorMessage error={errors.players?.message} withSidePadding={false} />
          )}
        </Box>
      )}

      <SearchUsersModal
        selectionLength={teamSize - players?.length}
        onClose={selectPlayerHandler}
        hiddenUsers={[...hiddenUsers, activeUser]}
      />
    </FormProvider>
  );
};
